import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  IndianRupee,
  RefreshCw,
  Crown,
  Building2,
  TrendingUp,
  History,
  AlertCircle,
  CheckCircle,
  Users,
  Car,
  Calendar,
} from "lucide-react";

export const CityAdminMyEarnings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    if (user?.id) fetchEarnings();
  }, [user?.id]);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/city-admin/${user.id}/earnings`);
      setEarnings(res.data);
    } catch (err) {
      toast.error("Failed to load earnings");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-admin-my-earnings">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IndianRupee className="w-6 h-6" /> My Earnings
          </h1>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Building2 className="w-4 h-4" /> {earnings?.city_name || "Your City"}
            {earnings?.is_primary && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium flex items-center gap-1">
                <Crown className="w-3 h-3" /> Primary
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={fetchEarnings}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Not Primary Warning */}
      {earnings?.not_primary_notice && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">Not Primary City Admin</h3>
              <p className="text-sm text-yellow-700">{earnings.not_primary_notice}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-blue-600">Drivers</span>
          </div>
          <p className="text-3xl font-bold text-blue-700">{earnings?.summary?.total_drivers || 0}</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-purple-600">Total Rides</span>
          </div>
          <p className="text-3xl font-bold text-purple-700">{earnings?.summary?.total_rides || 0}</p>
        </div>
        
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-5 h-5 text-teal-500" />
            <span className="text-sm text-teal-600">Fare Collected</span>
          </div>
          <p className="text-3xl font-bold text-teal-700">₹{earnings?.summary?.total_fare_collected?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-orange-600">Admin Commission</span>
          </div>
          <p className="text-3xl font-bold text-orange-700">₹{earnings?.summary?.admin_commission_collected?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Earnings & Payouts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* My Earnings */}
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-green-500" /> My Commission
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Commission Rate</span>
              <span className="font-bold text-lg">{earnings?.commission_percentage || 20}%</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Calculation</span>
              <span className="text-sm">{earnings?.earnings?.calculation}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Total Earnings</span>
              <span className="font-bold text-xl text-green-600">₹{earnings?.earnings?.total_earnings?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Today's Earnings</span>
              <span className="font-bold text-blue-600">₹{earnings?.earnings?.today_earnings?.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>

        {/* Payouts Summary */}
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-purple-500" /> Payouts
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Total Received</span>
              <span className="font-bold text-xl text-green-600">₹{earnings?.payouts?.total_received?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Pending Dues</span>
              <span className={`font-bold text-xl ${earnings?.payouts?.pending_dues > 0 ? "text-red-600" : "text-green-600"}`}>
                ₹{earnings?.payouts?.pending_dues?.toLocaleString() || 0}
              </span>
            </div>
            <div className="pt-2">
              {earnings?.payouts?.pending_dues > 0 ? (
                <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 p-2 rounded">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Contact Admin for pending payment</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">All dues cleared</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-card border rounded-lg">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <History className="w-5 h-5" /> Transaction History
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(earnings?.transactions || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No transactions yet
                </TableCell>
              </TableRow>
            ) : (
              earnings.transactions.map((txn) => (
                <TableRow key={txn.id}>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-muted-foreground" />
                      {new Date(txn.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      {txn.transaction_type === "payout" ? "Received" : txn.transaction_type}
                    </span>
                  </TableCell>
                  <TableCell className="capitalize">{txn.payment_method?.replace("_", " ")}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{txn.reference_id || "-"}</TableCell>
                  <TableCell className="text-right font-medium text-green-600">+₹{txn.amount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      txn.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}>
                      {txn.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CityAdminMyEarnings;
